/**
 * CIS / GOST engineering font → bundled OpenGost SDF-atlas mapping.
 *
 * Low-level, dependency-free module: it imports NOTHING from
 * `scenePlaceholder` (scene/) or `fontRegistry` (text/) so it can be
 * consumed by BOTH without creating an import cycle:
 *
 *   - `scene/scenePlaceholder.ts` (`resolveDispatchFontName`) routes a
 *     NON-SHX GOST/ISOCPEUR style name to the bundled Cyrillic atlas.
 *   - `text/fontRegistry.ts` (`resolveFont`) uses it as the SHX-fail
 *     fallback, so an UNREGISTERED `gost.shx` / `isocpeur` resolves to
 *     OpenGost (real Cyrillic glyphs) instead of the "?????" placeholder.
 *
 * # Why a dedicated module (extracted from `scenePlaceholder`)
 *
 * The mapping was born inside `resolveDispatchFontName`, but `resolveFont`
 * (text/) needs the SAME logic for its SHX-fail fallback. Importing it
 * from `scenePlaceholder` (scene/) into `fontRegistry` (text/) would form
 * a `text/ → scene/ → text/` cycle (scenePlaceholder already imports
 * `isShxFontName` from fontRegistry). Hoisting the pure mapping here keeps
 * a single source of truth with NO cycle.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1 — full fidelity, no hardcoded "Standard"
 *
 * AutoCAD STYLE records reference GOST fonts under many spellings — the
 * style name (`GOST-2.304_Type-B`), the Cyrillic style (`ГОСТ 2.304`,
 * `ГОСТ`), or the font file (`GOST2304_TypeB.ttf`, `gost.shx`). Each is
 * mapped from the ACTUAL name, never assumed.
 */

/**
 * CIS engineering SDF atlas names — OpenGost (SIL OFL 1.1) Type A / Type B,
 * the free GOST 2.304 implementation (see `apps/web/public/fonts/sources/
 * OFL.txt`). Built with a Cyrillic charset (`…/sources/charset.txt`) so
 * Russian drawing text renders real glyphs instead of the Latin-only
 * fallback.
 */
export const SDF_GOST_TYPE_A = "OpenGost-TypeA";
export const SDF_GOST_TYPE_B = "OpenGost-TypeB";

/**
 * Default Cyrillic-capable SDF atlas — the universal readable fallback.
 * `resolveDispatchFontName` returns it for unknown NON-SHX names, and
 * `resolveFont` falls back to it for an UNBUNDLED SHX (e.g. the AutoCAD
 * default `txt`, `romans.shx`) so CIS text stays readable instead of the
 * "?????" placeholder. Single source of truth so both callers agree.
 */
export const DEFAULT_SDF_FONT = "Roboto-Regular";

/**
 * Map a CIS/GOST or ISOCPEUR text-style / font-file name to a bundled
 * OpenGost atlas.
 *
 * Normalise (strip an AutoCAD width/oblique `$…` suffix, lowercase) and
 * route the request:
 *
 *   1. A direct reference to a bundled OpenGost source filename
 *      (`OpenGost-TypeA.ttf`, `opengost-typeb`) → that atlas. The
 *      "opengost" prefix fails the GOST-token guard below (preceded by
 *      "open"), so it is matched explicitly first.
 *   2. ISOCPEUR / ISOCP family → Type B (see substitution note below).
 *   3. A GOST token → Type A or Type B (Type B is the wider, more common
 *      GOST 2.304 variant and is the default when no variant is named).
 *
 * Returns `null` for non-CIS names so the caller keeps its existing
 * SHX / atlas / Roboto resolution.
 *
 * # ISOCPEUR / ISOCP substitution (subsumes #335)
 *
 * ISOCPEUR / ISOCP are AutoCAD's ISO 3098 stroke fonts. ISO 3098 is the
 * international engineering-lettering standard that GOST 2.304 derives
 * from, so their letterforms are visually near-identical. The real
 * ISOCPEUR.shx is proprietary (no free Cyrillic-complete equivalent
 * ships), so we substitute the bundled OpenGost-TypeB atlas — it covers
 * Cyrillic and matches the ISO 3098 ≈ GOST 2.304 proportions closely
 * enough to keep CIS drawings legible. A user who uploads the real
 * ISOCPEUR.shx via FontRegistry still wins (the SHX path is tried first
 * in both callers).
 */
export function mapCisFontToAtlas(name: string): string | null {
  const n = name.split("$")[0]?.trim().toLowerCase() ?? "";
  if (n === "") return null;
  // Direct reference to a bundled OpenGost atlas by its source filename
  // (any extension / case) — e.g. a STYLE whose font file is the shipped
  // `OpenGost-TypeA.ttf`. The "opengost" prefix fails the GOST-token guard
  // below (preceded by "open"), so match it explicitly first.
  const base = n.replace(/\.(ttf|otf|shx)$/, "");
  if (base === "opengost-typea") return SDF_GOST_TYPE_A;
  if (base === "opengost-typeb") return SDF_GOST_TYPE_B;
  // ISOCPEUR / ISOCP family (ISO 3098) — require the "isocp" token at the
  // start or after a non-letter so "isocpeur", "isocp", "isocp2",
  // "ISOCPEUR.shx" all match while an unrelated "…isocp…" inside a longer
  // word (preceded by a letter) does not. ISO 3098 ≈ GOST 2.304 → Type B.
  if (/(^|[^a-zа-яё])isocp/.test(n)) return SDF_GOST_TYPE_B;
  // Require GOST as a TOKEN: "gost" / "гост" at the start or after a
  // non-letter AND not followed by another letter — a raw substring would
  // wrongly capture "Agostino" (preceding letter) or "Gostino" (following
  // letter). Digits are fine ("gost2304"), so the variant suffix still matches.
  if (!/(^|[^a-zа-яё])(gost|гост)(?![a-zа-яё])/.test(n)) return null;
  // Variant letter: explicit "type a/b" / "тип а/б", OR a trailing a/b (Latin
  // OR Cyrillic а/б) right after the gost token — e.g. gost2304a, гост2304а,
  // гост-а, GOST type B. `(?![a-zа-яё])` keeps it a variant suffix, not part of
  // a longer word. Type A only when A matches and B does not; everything else
  // GOST (explicit B, generic ГОСТ, gost.shx) → Type B (the wider default).
  const isA =
    /type[\s._-]*a|тип[\s._-]*а/.test(n) ||
    /(gost|гост)[\s._\-0-9]*[aа](?![a-zа-яё])/.test(n);
  const isB =
    /type[\s._-]*b|тип[\s._-]*б/.test(n) ||
    /(gost|гост)[\s._\-0-9]*[bб](?![a-zа-яё])/.test(n);
  if (isA && !isB) return SDF_GOST_TYPE_A;
  return SDF_GOST_TYPE_B;
}
